import { useCallback, useEffect, useState } from "react";
import { api, fmtDate, formatApiError } from "../lib/api";
import { useAuth } from "../lib/auth";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import { WhatsappShareButton } from "../components/WhatsappShareButton";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "../components/ui/dropdown-menu";
import { Pin, PinOff, Plus, MoreVertical } from "lucide-react";
import { toast } from "sonner";

export default function NoticesPage() {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [creating, setCreating] = useState(false);
  const [title, setTitle] = useState(""); const [body, setBody] = useState("");
  const [audience, setAudience] = useState("all"); const [pinned, setPinned] = useState(false);

  const load = useCallback(async () => { const r = await api.get("/notices"); setItems(r.data); }, []);
  useEffect(() => { load(); }, [load]);

  async function create(e) {
    e.preventDefault();
    try {
      await api.post("/notices", { title, body, audience, pinned });
      toast.success("Notice posted"); setCreating(false); setTitle(""); setBody(""); setPinned(false);
      load();
    } catch (err) { toast.error(formatApiError(err)); }
  }

  async function ack(id) { await api.post(`/notices/${id}/ack`); load(); }

  async function togglePin(n) {
    try { await api.patch(`/notices/${n.id}`, { pinned: !n.pinned }); load(); toast.success(n.pinned ? "Unpinned" : "Pinned"); }
    catch (err) { toast.error(formatApiError(err)); }
  }

  return (
    <div className="px-5 md:px-10 py-6 md:py-10 max-w-3xl">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <div className="text-xs uppercase tracking-widest text-stone-500 font-bold">Notices</div>
          <h1 className="font-serif-display text-3xl md:text-4xl font-semibold mt-1">Building notices</h1>
        </div>
        {user?.role === "committee" && (
          <Button onClick={() => setCreating(!creating)} data-testid="new-notice-btn"
            className="rounded-full bg-[#C05640] hover:bg-[#A54734]"><Plus size={18} className="mr-1" /> New notice</Button>
        )}
      </div>

      {creating && (
        <form onSubmit={create} className="mt-5 bg-white border border-stone-200 rounded-2xl p-5 space-y-3" data-testid="notice-form">
          <div><Label>Title</Label><Input value={title} required onChange={(e) => setTitle(e.target.value)} data-testid="n-title" className="h-11 mt-1" /></div>
          <div><Label>Body</Label><Textarea rows={4} required value={body} onChange={(e) => setBody(e.target.value)} data-testid="n-body" className="mt-1" /></div>
          <div className="grid md:grid-cols-2 gap-3 items-end">
            <div>
              <Label>Audience</Label>
              <select value={audience} onChange={(e) => setAudience(e.target.value)} className="w-full h-11 mt-1 rounded-xl border border-stone-300 px-3" data-testid="n-aud">
                <option value="all">Everyone</option>
                <option value="owners">Owners only</option>
                <option value="tenants">Tenants only</option>
                <option value="staff">Staff only</option>
                <option value="committee">Committee only</option>
              </select>
            </div>
            <label className="inline-flex items-center gap-2 text-sm">
              <input type="checkbox" checked={pinned} onChange={(e) => setPinned(e.target.checked)} data-testid="n-pin" className="w-5 h-5 accent-[#C05640]" />
              Pin to top
            </label>
          </div>
          <Button type="submit" className="rounded-full bg-stone-900 text-white" data-testid="n-save">Post notice</Button>
        </form>
      )}

      <div className="mt-6 space-y-3">
        {items.map((n) => (
          <div key={n.id} className={`bg-white rounded-2xl p-5 border ${n.pinned ? "border-[#C05640]" : "border-stone-200"}`} data-testid={`notice-card-${n.id}`}>
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0">
                {n.pinned && <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-widest font-bold text-[#C05640]"><Pin size={10} /> Pinned</span>}
                <h3 className="font-serif-display text-xl font-medium mt-0.5">{n.title}</h3>
                <p className="text-stone-700 text-sm mt-1.5 whitespace-pre-wrap">{n.body}</p>
                <div className="text-xs text-stone-500 mt-2 capitalize">{n.audience} · {fmtDate(n.created_at)} · {n.ack_count || 0} acknowledged</div>
              </div>
              {user?.role === "committee" && (
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <button data-testid={`notice-menu-${n.id}`} className="p-2 text-stone-400 hover:text-stone-700"><MoreVertical size={18} /></button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent>
                    <DropdownMenuItem onClick={() => togglePin(n)} data-testid={`notice-pin-${n.id}`}>
                      {n.pinned ? <><PinOff size={14} className="mr-2" />Unpin</> : <><Pin size={14} className="mr-2" />Pin to top</>}
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              )}
            </div>
            <div className="mt-4 flex items-center gap-2 flex-wrap">
              {!n.acked && <Button onClick={() => ack(n.id)} variant="outline" size="sm" className="rounded-full" data-testid={`ack-${n.id}`}>I've read it</Button>}
              {n.acked && <span className="text-xs text-emerald-700 font-medium">✓ Acknowledged</span>}
              <WhatsappShareButton entityType="notice" entityId={n.id}
                fallbackText={`*Notice* — ${n.title}\n\n${n.body}\n\n— via NivasOne`} className="ml-auto" testid={`wa-notice-${n.id}`} label="Share" />
            </div>
          </div>
        ))}
        {items.length === 0 && <div className="text-stone-500 italic py-8 text-center">No notices yet.</div>}
      </div>
    </div>
  );
}
